class DesktopMegaMenu extends HTMLElement {
  constructor() {
    super();

    this.toggleButton = null;
    this.toggleLabel = null;

    this.underlay = null;
    this.closeButton = null;

    this.parentButtons = [];
    this.panels = [];

    this.isOpen = false;
    this.closeTimer = null;

    this.desktopMedia =
      window.matchMedia(
        '(min-width: 750px)'
      );

    this.handleToggle =
      this.handleToggle.bind(this);

    this.handleUnderlayClick =
      this.handleUnderlayClick.bind(this);

    this.handleCloseClick =
      this.handleCloseClick.bind(this);

    this.handleDocumentKeydown =
      this.handleDocumentKeydown.bind(this);

    this.handleParentInteraction =
      this.handleParentInteraction.bind(this);

    this.handleViewportChange =
      this.handleViewportChange.bind(this);
  }

  connectedCallback() {
    const menuId =
      this.id;

    this.toggleButton =
      document.querySelector(
        `[data-desktop-mega-menu-toggle][aria-controls="${CSS.escape(
          menuId
        )}"]`
      );

    this.toggleLabel =
      this.toggleButton?.querySelector(
        '[data-desktop-mega-menu-label]'
      );

    this.underlay =
      this.querySelector(
        '[data-desktop-mega-menu-underlay]'
      );

    this.closeButton =
      this.querySelector(
        '[data-desktop-mega-menu-close]'
      );

    this.parentButtons =
      Array.from(
        this.querySelectorAll(
          '[data-desktop-mega-menu-parent]'
        )
      );

    this.panels =
      Array.from(
        this.querySelectorAll(
          '[data-desktop-mega-menu-panel]'
        )
      );

    this.toggleButton?.addEventListener(
      'click',
      this.handleToggle
    );

    this.underlay?.addEventListener(
      'click',
      this.handleUnderlayClick
    );

    this.closeButton?.addEventListener(
      'click',
      this.handleCloseClick
    );

    document.addEventListener(
      'keydown',
      this.handleDocumentKeydown
    );

    this.desktopMedia.addEventListener?.(
      'change',
      this.handleViewportChange
    );

    this.parentButtons.forEach(
      (button) => {
        button.addEventListener(
          'mouseenter',
          this.handleParentInteraction
        );

        button.addEventListener(
          'focus',
          this.handleParentInteraction
        );

        button.addEventListener(
          'click',
          this.handleParentInteraction
        );
      }
    );
  }

  disconnectedCallback() {
    this.toggleButton?.removeEventListener(
      'click',
      this.handleToggle
    );

    this.underlay?.removeEventListener(
      'click',
      this.handleUnderlayClick
    );

    this.closeButton?.removeEventListener(
      'click',
      this.handleCloseClick
    );

    document.removeEventListener(
      'keydown',
      this.handleDocumentKeydown
    );

    this.desktopMedia.removeEventListener?.(
      'change',
      this.handleViewportChange
    );

    this.parentButtons.forEach(
      (button) => {
        button.removeEventListener(
          'mouseenter',
          this.handleParentInteraction
        );

        button.removeEventListener(
          'focus',
          this.handleParentInteraction
        );

        button.removeEventListener(
          'click',
          this.handleParentInteraction
        );
      }
    );
  }

  handleViewportChange(event) {
    if (!event.matches) {
      this.close(true);
    }
  }

  handleToggle() {
    if (
      !this.desktopMedia.matches
    ) {
      return;
    }

    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  handleUnderlayClick() {
    this.close();
  }

  handleCloseClick() {
    this.close();

    this.toggleButton?.focus();
  }

  handleDocumentKeydown(event) {
    if (
      event.key !== 'Escape' ||
      !this.isOpen
    ) {
      return;
    }

    this.close();

    this.toggleButton?.focus();
  }

  handleParentInteraction(event) {
    if (
      !this.desktopMedia.matches
    ) {
      return;
    }

    const button =
      event.currentTarget;

    if (
      !(button instanceof HTMLElement)
    ) {
      return;
    }

    this.activatePanel(
      button.dataset.panelId
    );
  }

  activatePanel(panelId) {
    if (!panelId) {
      return;
    }

    this.parentButtons.forEach(
      (button) => {
        const isActive =
          button.dataset.panelId ===
          panelId;

        button.classList.toggle(
          'is-active',
          isActive
        );

        button.setAttribute(
          'aria-expanded',
          String(isActive)
        );
      }
    );

    this.panels.forEach(
      (panel) => {
        const isActive =
          panel.id ===
          panelId;

        panel.classList.toggle(
          'is-active',
          isActive
        );

        panel.hidden =
          !isActive;
      }
    );
  }

  open() {
    if (
      this.isOpen ||
      !this.desktopMedia.matches
    ) {
      return;
    }

    if (this.closeTimer) {
      window.clearTimeout(
        this.closeTimer
      );

      this.closeTimer =
        null;
    }

    this.isOpen =
      true;

    this.hidden =
      false;

    requestAnimationFrame(
      () => {
        this.classList.add(
          'is-open'
        );
      }
    );

    this.toggleButton?.setAttribute(
      'aria-expanded',
      'true'
    );

    if (this.toggleLabel) {
      this.toggleLabel.textContent =
        'Cerrar';
    }

    document.documentElement.classList.add(
      'desktop-mega-menu-open'
    );
  }

  close(immediate = false) {
    this.isOpen =
      false;

    this.classList.remove(
      'is-open'
    );

    this.toggleButton?.setAttribute(
      'aria-expanded',
      'false'
    );

    if (this.toggleLabel) {
      this.toggleLabel.textContent =
        'Menú';
    }

    document.documentElement.classList.remove(
      'desktop-mega-menu-open'
    );

    if (immediate) {
      this.hidden =
        true;

      return;
    }

    this.closeTimer =
      window.setTimeout(
        () => {
          if (!this.isOpen) {
            this.hidden =
              true;
          }
        },
        220
      );
  }
}

if (
  !customElements.get(
    'desktop-mega-menu'
  )
) {
  customElements.define(
    'desktop-mega-menu',
    DesktopMegaMenu
  );
}